import apiClient from './client';
import { buildJsonApiQuery, normalizeErrors } from '@/utils/jsonapi';

/**
 * A synchronous PDF render can outlast the client's 30s default (E18). The
 * production ALB closes idle connections at 60s, so a longer client timeout
 * never fires: wait just past it and let the ALB's 502/504 (or dropped
 * connection) arrive. Infra follow-up: raise the ALB idle timeout, then this
 * can go to 120s.
 */
export const PDF_EXPORT_TIMEOUT_MS = 65000;

/** A failure this long after the request started is the load balancer giving up. */
export const PDF_EXPORT_SLOW_FAILURE_MS = 55000;

const GATEWAY_TIMEOUT_STATUSES = [502, 504];

/**
 * Whether a failed PDF export ran out of time rather than failed outright:
 * the client timeout, a 502/504 from the load balancer, or a network error
 * after `PDF_EXPORT_SLOW_FAILURE_MS` (the ALB dropping the connection).
 *
 * @param {Object} error - the rejected axios error (`apiError` attached)
 * @param {number} elapsedMs - time since the export started
 * @returns {boolean}
 */
export function isPdfExportTimeout(error, elapsedMs) {
  const apiError = error?.apiError;
  if (apiError?.isTimeout) return true;
  if (GATEWAY_TIMEOUT_STATUSES.includes(apiError?.status)) return true;
  return apiError?.code === 'network_error' && elapsedMs >= PDF_EXPORT_SLOW_FAILURE_MS;
}

/**
 * Flatten a single JSON:API resource response into { id, ...attributes }.
 * Shared by any report sub-resource (parameters, readiness) that accepts a
 * flat body but always renders the full `{data: {type, id, attributes}}`
 * envelope on the way out.
 *
 * @param {Object} payload - Raw `response.data`
 * @returns {Object} Flat resource object
 */
function unwrapResource(payload) {
  const resource = payload?.data;
  if (!resource) return payload || {};
  return { id: resource.id, ...(resource.attributes || {}) };
}

/**
 * Pull a filename out of a Content-Disposition header value.
 *
 * Handles the plain quoted form (`filename="x.pdf"`) and the RFC 5987
 * extended form (`filename*=UTF-8''x.pdf`), preferring the latter when both
 * are present since it is the encoding-safe one. Falls back to a default
 * when the header is missing or doesn't parse.
 *
 * @param {string} [headerValue] - Raw Content-Disposition header value
 * @param {string} fallback - Filename to use when none can be parsed
 * @returns {string} Filename to save the download as
 */
function filenameFromContentDisposition(headerValue, fallback) {
  if (!headerValue) return fallback;

  const extendedMatch = headerValue.match(/filename\*=UTF-8''([^;]+)/i);
  if (extendedMatch) {
    try {
      return decodeURIComponent(extendedMatch[1]);
    } catch {
      // Malformed percent-encoding - fall through to the quoted match.
    }
  }

  const quotedMatch = headerValue.match(/filename="?([^";]+)"?/i);
  return quotedMatch ? quotedMatch[1] : fallback;
}

/**
 * Recover the server's real error detail for a failed `responseType: 'blob'`
 * request.
 *
 * Axios applies `responseType: 'blob'` to error responses too, so a JSON
 * error body (e.g. `{errors: [{detail: 'No PDF template configured...'}]}`)
 * arrives as an opaque Blob. The client interceptor's `normalizeErrors` runs
 * synchronously and can't read a Blob, so it falls through to a generic
 * "unexpected error" and `err.apiError.message` never carries the server's
 * actual detail. Read the Blob back out as JSON here and re-derive
 * `apiError` through the same normalization the interceptor uses, so
 * callers reading `err.apiError.message` see the real detail.
 *
 * @param {Object} err - Axios error, mutated in place with a corrected
 *   `apiError` when the body turns out to be parseable JSON
 * @returns {Promise<void>}
 */
async function deriveApiErrorFromBlobBody(err) {
  const response = err?.response;
  const blob = response?.data;
  if (!(blob instanceof Blob) || !/json/i.test(blob.type || '')) return;

  try {
    const data = JSON.parse(await blob.text());
    const normalizedErrors = normalizeErrors({ status: response.status, data });
    err.apiError = {
      status: response.status,
      code: normalizedErrors[0]?.code || data?.code || data?.error,
      message: normalizedErrors.length > 0 ? normalizedErrors[0]?.detail : err.message,
      errors: normalizedErrors,
      details: data?.details,
    };
  } catch {
    // Not parseable JSON after all - leave whatever apiError already exists.
  }
}

/**
 * Reports API - Handles all report-related API calls
 * Uses JSON:API format for requests/responses
 */
const reportsAPI = {
  async previewDraft(draft, options = {}) {
    const response = await apiClient.post('/reports/builder-preview/', draft, options);
    return unwrapResource(response.data);
  },

  async saveDraft(draft) {
    const response = await apiClient.post('/reports/builder-save/', draft);
    return unwrapResource(response.data);
  },

  async getMetricConfigs(id) {
    const response = await apiClient.get(`/reports/${id}/metrics/`);
    return response.data;
  },

  /**
   * Get all reports with optional pagination
   * @param {Object} options - Query options
   * @param {number} options.page - Page number
   * @param {number} options.pageSize - Items per page
   * @param {Object} options.filters - Filter criteria
   * @param {string} options.include - Related resources to include (e.g., 'metrics,owner')
   * @returns {Promise<Object>} - { data: [...], meta: { pagination }, links }
   */
  async getAll(options = {}, requestOptions = {}) {
    const params = buildJsonApiQuery(options);
    const response = await apiClient.get('/reports/', { ...requestOptions, params });

    // Return raw JSON:API response (normalization moved to slice)
    return response.data;
  },

  /**
   * Get a single report by ID
   * @param {string} id - Report ID
   * @param {Object} options - Query options
   * @param {string} options.include - Related resources to include (e.g., 'metrics,owner')
   * @returns {Promise<Object>} - Normalized report object
   */
  async getById(id, options = {}) {
    const params = buildJsonApiQuery(options);
    const response = await apiClient.get(`/reports/${id}/`, { params });

    // Return raw JSON:API response (normalization moved to slice)
    return response.data;
  },

  /**
   * Create a new report
   *
   * `metrics` are NOT a JSON:API relationship on this resource - the
   * serializer's writable field is the plain attribute `metric_configs`
   * (an array of `{metric_id, sort_order, display_config}`; see
   * `reportsSlice.js`'s `buildReportPayload`, the real caller). Whatever the
   * caller puts on `reportData.metric_configs` is passed through as-is.
   *
   * @param {Object} reportData - Report data
   * @param {string} reportData.name - Report name
   * @param {string} reportData.description - Report description
   * @param {boolean} reportData.is_organization_wide - Visibility
   * @param {Object} reportData.configuration - Report configuration
   * @param {Array<Object>} [reportData.metric_configs] - Metric config
   *   objects: `{metric_id, sort_order, display_config}`
   * @returns {Promise<Object>} - Normalized created report
   */
  async create(reportData) {
    const payload = {
      data: {
        type: 'report',
        attributes: reportData,
      },
    };

    const response = await apiClient.post('/reports/', payload);

    // Return raw JSON:API response (normalization moved to slice)
    return response.data;
  },

  /**
   * Update an existing report
   *
   * `metrics` are NOT a JSON:API relationship on this resource - the
   * serializer's writable field is the plain attribute `metric_configs`
   * (an array of `{metric_id, sort_order, display_config}`; see
   * `reportsSlice.js`'s `buildReportPayload`, the real caller). Whatever the
   * caller puts on `updates.metric_configs` is passed through as-is.
   *
   * @param {string} id - Report ID
   * @param {Object} updates - Updated report data
   * @param {string} updates.name - Report name
   * @param {string} updates.description - Report description
   * @param {boolean} updates.is_organization_wide - Visibility
   * @param {Object} updates.configuration - Report configuration
   * @param {Array<Object>} [updates.metric_configs] - Metric config
   *   objects: `{metric_id, sort_order, display_config}`
   * @returns {Promise<Object>} - Normalized updated report
   */
  async update(id, updates) {
    const payload = {
      data: {
        type: 'report',
        id,
        attributes: updates,
      },
    };

    const response = await apiClient.patch(`/reports/${id}/`, payload);

    // Return raw JSON:API response (normalization moved to slice)
    return response.data;
  },

  /**
   * Delete a report
   * @param {number} id - Report ID
   * @returns {Promise<void>}
   */
  async delete(id) {
    await apiClient.delete(`/reports/${id}/`);
  },

  /**
   * Duplicate an existing report
   * @param {number} id - Report ID to duplicate
   * @returns {Promise<Object>} - JSON:API formatted duplicated report
   */
  async duplicate(id) {
    const response = await apiClient.post(`/reports/${id}/duplicate/`);
    return response.data;
  },

  /**
   * Get a report's execution parameters (date range, grouping, filters).
   *
   * These live on a separate ReportParameter record — not on the report's
   * `configuration` attribute — and are what report generation actually reads.
   *
   * @param {string} id - Report ID
   * @returns {Promise<Object>} Report parameters
   */
  async getParameters(id) {
    const response = await apiClient.get(`/reports/${id}/parameters/`);
    return unwrapResource(response.data);
  },

  /**
   * Update a report's execution parameters.
   * Only the report owner may update parameters (403 otherwise).
   *
   * @param {string} id - Report ID
   * @param {Object} parameters - Partial ReportParameter fields
   * @returns {Promise<Object>} Updated report parameters
   */
  async updateParameters(id, parameters) {
    const response = await apiClient.patch(`/reports/${id}/parameters/`, parameters);
    return unwrapResource(response.data);
  },

  /**
   * Report readiness pre-flight (R-10/F-11): what, if anything, stands
   * between this report and Generate - no metrics, a metric with no data in
   * the date range, mismatched partition types, mixed metric types, or a
   * saved partition filter that would exclude a metric. Read-only; any
   * tenant member who can already read the report may call it.
   *
   * @param {string} id - Report ID
   * @returns {Promise<Object>} Flat `{id, date_range, metrics, warnings, can_generate}`
   */
  async getReadiness(id) {
    const response = await apiClient.get(`/reports/${id}/readiness/`);
    return unwrapResource(response.data);
  },

  /**
   * Generate a report and get results
   * @param {number} id - Report ID
   * @param {Object} params - Generation parameters (date range, filters, etc.)
   * @returns {Promise<Object>} - Report generation results with columns and rows
   */
  async generate(id, params = {}, options = {}) {
    const response = await apiClient.post(`/reports/${id}/generate/`, params, options);
    return response.data;
  },

  /**
   * Get report schedules
   * @param {number} id - Report ID
   * @returns {Promise<Array>} - List of schedules for the report
   */
  async getSchedules(id) {
    const response = await apiClient.get(`/reports/${id}/schedules/`);
    return response.data;
  },

  /**
   * Export report data to Excel
   * @param {number} id - Report ID
   * @param {Object} params - Export parameters
   * @returns {Promise<Blob>} - File blob for download
   */
  async exportExcel(id, params = {}, options = {}) {
    const response = await apiClient.post(
      `/reports/${id}/export/excel/`,
      params,
      { ...options, responseType: 'blob' }
    );
    return response.data;
  },

  /**
   * Export a report as a PDF rendered from its assigned template and its
   * own generated data (no client-supplied context - B-6 builds it
   * server-side from the report itself).
   *
   * @param {string} id - Report ID
   * @returns {Promise<{blob: Blob, filename: string}>} PDF blob and the
   *   filename the server suggested via Content-Disposition
   */
  async exportPdf(id) {
    try {
      const response = await apiClient.post(
        `/reports/${id}/export/pdf/`,
        {},
        // E18: the PDF renders synchronously and a large report outlasts the
        // client's 30s default.
        { responseType: 'blob', timeout: PDF_EXPORT_TIMEOUT_MS }
      );
      return {
        blob: response.data,
        filename: filenameFromContentDisposition(
          response.headers?.['content-disposition'],
          'report.pdf'
        ),
      };
    } catch (err) {
      await deriveApiErrorFromBlobBody(err);
      throw err;
    }
  },

  /**
   * Export a report as CSV, streamed straight from its generated data (H1).
   * Same gate/access rules as the Excel export - GET, not POST, since there
   * is no client-supplied export context to send.
   *
   * @param {string} id - Report ID
   * @returns {Promise<{blob: Blob, filename: string}>} CSV blob and the
   *   filename the server suggested via Content-Disposition
   */
  async exportCsv(id) {
    try {
      const response = await apiClient.get(`/reports/${id}/export/csv/`, {
        responseType: 'blob',
      });
      return {
        blob: response.data,
        filename: filenameFromContentDisposition(
          response.headers?.['content-disposition'],
          'report.csv'
        ),
      };
    } catch (err) {
      await deriveApiErrorFromBlobBody(err);
      throw err;
    }
  },
};

export default reportsAPI;
